import { AnimatePresence, motion } from "framer-motion";
import { Navigate } from "react-router-dom";
import { shouldRedirectToHelpPage } from "./wrapUpNavigation";
import { ArrowLeft, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { IPhoneTapReminder } from "@/components/mobile/IPhoneTapReminder";
import { Button } from "@/components/ui/button";
import { NoiseEnvironmentSheet } from "@/components/voice/NoiseEnvironmentSheet";
import { cn } from "@/lib/utils";
import { isDarkBackground, widgetBackgroundToCss } from "@/lib/widgetBackgroundPresets";
import { fullPageChatAppearance, popupBackground } from "@/lib/fullPageChatAppearance";
import type { UseFormReturn } from "react-hook-form";
import type { CallbackRequestErrorCode, EmailQuestionErrorCode } from "@/hooks/voice/useChannelRequests";
import type { ConversationEmailPurpose, FollowUpFormValues } from "@/types/voice/conversation-follow-up";
import { AgentPageProps } from "@/types/agent/agent-page";
import { InfoLinksConfig } from "@/types/agent/info-links";
import type { HelpHubDTO } from "@/types/help-hub/help-hub";
import { CallStage } from "@/pages/agent-interfaces/live-agent/components/CallStage";
import { EmailStage } from "@/pages/agent-interfaces/live-agent/components/EmailStage";
import { ConversationFollowUpSheet } from "@/pages/agent-interfaces/live-agent/components/ConversationFollowUpSheet";
import { ConversationWrapUp } from "@/pages/agent-interfaces/live-agent/components/ConversationWrapUp";
import { GenieConversationCanvas } from "@/pages/agent-interfaces/live-agent/components/GenieConversationCanvas";
import { GenieDetailsPanel } from "@/pages/agent-interfaces/live-agent/components/GenieDetailsPanel";
import { GenieDetailsTab } from "@/pages/agent-interfaces/live-agent/components/GenieDetailsTab";
import { GenieTextChatOverlayWeb } from "@/pages/agent-interfaces/live-agent/components/GenieTextChatOverlayWeb";
import { GenieWelcomeScreen } from "@/pages/agent-interfaces/live-agent/components/GenieWelcomeScreen";
import { HumanHandoffPrompt } from "@/pages/agent-interfaces/live-agent/components/HumanHandoffPrompt";
import type { TranscriptMessage } from "@/pages/agent-interfaces/live-agent/components/TranscriptPanel";
import { ResourcesRail } from "@/pages/agent-interfaces/live-agent/components/ResourcesRail";
import { ResourcesSheet } from "@/pages/agent-interfaces/live-agent/components/ResourcesSheet";
import { VoiceSessionActionRow } from "@/pages/agent-interfaces/live-agent/components/VoiceSessionActionRow";
import { VoiceTroubleSheet } from "@/pages/agent-interfaces/live-agent/components/VoiceTroubleSheet";
import { VoiceTroubleTrigger } from "@/pages/agent-interfaces/live-agent/components/VoiceTroubleTrigger";
import LiveAgentContainer from "@/pages/agent-interfaces/live-agent/LiveAgentContainer";
import { useDesktopStageFit } from "@/pages/agent-interfaces/live-agent/useDesktopStageFit";
import MobileLiveAgentLayout from "@/pages/agent-interfaces/live-agent/mobile/MobileLiveAgentLayout";
import { AgentCore, AgentCoreProps } from "./AgentCore";
import { EmbedAttributionFooter } from "./EmbedAttributionFooter";
import { MobileAwareBottomInput } from "./MobileAwareBottomInput";
import { PauseOverlay } from "./PauseOverlay";
import { StudioHeaderActions } from "./StudioHeaderActions";
import { StudioActionCluster } from "./StudioActionCluster";

/**
 * Width of the desktop resources-rail gutter. Applied to the rail column and
 * to the matching spacer on the far side, so the genie column stays optically
 * centred whether or not the transcript is open.
 */
const RAIL_GUTTER_CLASS = "flex-[0_0_272px] self-center";
// Stands where the transcript column would be while it is closed; scrolls on
// its own when the Genie shares more than fits beside the avatar.
const SHARED_MEDIA_COLUMN_CLASS =
  "flex flex-[0_1_400px] min-w-[280px] justify-center self-center max-h-[calc(100dvh-140px)] overflow-y-auto";

type AttachmentItem = {
  id: string;
  file: File;
  previewUrl: string;
  status: "analyzing" | "ready" | "error";
  analysisText?: string;
};

/**
 * The no-template default view: mobile / conversation-canvas / desktop
 * three-way layout switch, bottom input, attribution footer, lead-capture
 * modals, iPhone tap reminder, noise sheet, resources sheet, voice-trouble
 * trigger/sheet, details scrim+tab+panel, wrap-up, handoff prompt, welcome
 * screen, pause overlay, and hidden file inputs.
 */
export interface StudioDefaultBranchProps {
  agent: AgentPageProps["agentPageData"] | null;
  brand: string;
  isEmbedMode: boolean;
  demoMode?: boolean;
  portalMode: boolean;
  isMobile: boolean;
  isInternalAdmin: boolean;
  conversationCanvasActive: boolean;
  /** What the Genie shared, shown in the transcript's column while it is closed (desktop only). */
  sharedMediaColumn?: React.ReactNode;
  helmet: React.ReactNode;
  onScreenTouch: () => void;
  agentLogo?: string | null;
  studioUrl: string;

  headerActionsProps: {
    onBack: () => void;
    onShare: () => void;
  };

  /** Base props shared with AgentCore across every surface. */
  agentCoreBaseProps: Omit<AgentCoreProps, "statusMessage" | "onOpenChat">;
  getStatusMessage: () => string;

  layoutState: {
    messages: TranscriptMessage[];
    /** `messages` with the suggested resources placed in it as cards (see useSuggestedResourceMessages). */
    chatMessages: TranscriptMessage[];
    showTranscript: boolean;
    setShowTranscript: (show: boolean) => void;
    startedFromText: boolean;
    isTextOnlyMode: boolean;
    isTranscriptEnabled: () => boolean;
    chatExpanded: boolean;
    setChatExpanded: (updater: boolean | ((v: boolean) => boolean)) => void;
    inlineMessage: string;
    setInlineMessage: (v: string) => void;
    onSendChat: () => void;
    isMicMuted: boolean;
    onToggleMicMute: () => void;
    isAgentVoiceMuted: boolean;
    onToggleAgentVoice: () => void;
    isPaused: boolean;
    onTogglePause: () => void;
    isAllAudioMuted: boolean;
    onToggleAllAudioMute: () => void;
    onEndConversation: () => void;
    onEndChat: () => void;
    inlineCaptureNode: React.ReactNode;
  };

  resourcesRail: {
    railItems: unknown[];
    resourcesConfig: InfoLinksConfig | null;
    surfacedResourceId?: string | null;
    onOpenAll: () => void;
    onSelect: (id: string) => void;
    onImpression: (id: string) => void;
    railInGutter: boolean;
  };

  attachmentsState: {
    attachments: AttachmentItem[];
    onRemoveAttachment: (id: string) => void;
    onUpdateAttachmentAnalysis: (id: string, text: string) => void;
    pendingImageUploads: number;
    failedImageUploads: number;
    isSavingImages: boolean;
    onRetryImageUploads: () => void;
    onAddFiles: (files: FileList | null) => void;
  };

  mobileLayoutProps: {
    forceTranscriptOpen: boolean;
    suppressTranscript: boolean;
    /** What the Genie shared, shown beneath the action row while the drawer is closed. */
    sharedMedia?: React.ReactNode;
    onDrawerOpenChange: (open: boolean) => void;
    mobileDrawerOpen: boolean;
  };

  modalsProps: {
    lastConversationId: string | null;
    tapReminder: {
      showReminder: boolean;
      dismissReminder: () => void;
    };
    showNoiseWarning: boolean;
    dismissNoiseWarning: () => void;
    onNoiseSwitchToText: () => void;
    showResources: boolean;
    onCloseResources: () => void;
    showVoiceTroubleTrigger: boolean;
    setShowVoiceTrouble: (show: boolean) => void;
    showVoiceTrouble: boolean;
    phoneNumber: string | null;
    onContactCaptured: (email: string) => void;
  };

  detailsProps: {
    showDetails: boolean;
    setShowDetails: (show: boolean) => void;
    showDetailsAffordance: boolean;
    hasResources: boolean;
    helpPageUrl: string | null;
    helpHub?: HelpHubDTO | null;
    canManageCustomerMemory: boolean;
    memoryUpdating: boolean;
    onMemoryConsentChange: (enabled: boolean) => void;
  };

  wrapUpProps: {
    showWrapUp: boolean;
    setShowWrapUp: (show: boolean) => void;
    onStartOver: () => void;
    onSwitchToText: () => void;
  };

  handoffProps: {
    isVoiceBridgeEnabled: boolean;
    showHandoffPrompt: boolean;
    setShowHandoffPrompt: (open: boolean) => void;
    setHandoffError: (error: string | null) => void;
    handoffError: string | null;
    bridgeStatus: string;
    onConfirmHandoff: () => void;
  };

  welcomeProps: {
    showWelcome: boolean;
    welcomeSeenForGenie: boolean;
    showInviteGate: boolean;
    canManageCustomerMemory: boolean;
    onStartVoice: () => void;
    onStartText: () => void;
  };

  pauseOverlayProps: {
    isPaused: boolean;
    onResume: () => void;
    onEndConversation: () => void;
  };

  channelProps: {
    channel: "voice" | "chat" | "phone" | "email";
    setChannel: (channel: "voice" | "chat" | "phone" | "email") => void;
    phoneNumber: string | null;
    mailboxAddress: string | null;
    onCallGenie: () => void;
    onEmailGenie: () => void;
    callbackAvailable: boolean;
    onRequestCallback: (args: {
      phoneE164: string;
      visitorName?: string;
      pageUrl: string;
    }) => Promise<{ ok: boolean; errorCode?: CallbackRequestErrorCode; errorMessage?: string }>;
    onAskQuestion: (args: {
      visitorEmail: string;
      visitorName?: string;
      question: string;
      pageUrl: string;
    }) => Promise<{ ok: boolean; errorCode?: EmailQuestionErrorCode; errorMessage?: string }>;
    channelPageUrl: string;
    helpPageUrl: string | null;
  };

  followUpProps: {
    fullTextView: boolean;
    setFullTextView: (v: boolean) => void;
    emailSummaryAction: boolean;
    emailSummaryPending: boolean;
    emailSummaryRequested: boolean;
    onEmailSummary?: () => void;
    onEmailFollowUp?: () => void;
    open: boolean;
    onClose: () => void;
    form: UseFormReturn<FollowUpFormValues>;
    purpose?: ConversationEmailPurpose;
    contextTruncated?: boolean;
    onSubmit: () => void;
    isPending: boolean;
    isSuccess: boolean;
    error: string | null;
  };
}

export function StudioDefaultBranch({
  agent,
  brand,
  isEmbedMode,
  demoMode,
  portalMode,
  isMobile,
  isInternalAdmin,
  conversationCanvasActive,
  sharedMediaColumn,
  helmet,
  onScreenTouch,
  agentLogo,
  studioUrl,
  headerActionsProps,
  agentCoreBaseProps,
  getStatusMessage,
  layoutState,
  resourcesRail,
  attachmentsState,
  mobileLayoutProps,
  modalsProps,
  detailsProps,
  wrapUpProps,
  handoffProps,
  welcomeProps,
  pauseOverlayProps,
  channelProps,
  followUpProps,
}: StudioDefaultBranchProps) {
  const stageFit = useDesktopStageFit();
  const mappedAttachments = attachmentsState.attachments.map((att) => ({
    id: att.id,
    previewUrl: att.previewUrl,
    status: att.status,
    analysisText: att.analysisText,
  }));

  const openFilePicker = () => {
    const input = document.getElementById("genie-chat-attachments") as HTMLInputElement | null;
    if (input) {
      input.click();
    }
  };

  // Full Page Appearance styles this page, embedded or not. Embedded, the
  // popup's settings fill in whatever the full page never set (the embed*
  // values in agentCoreBaseProps are those popup values, and are null on the
  // regular page). Chat appearance is shared with the Live Agent website
  // surfaces through the same presentation contract.
  const embedStyle = agent?.branding?.embedStyle;
  const fullPageStyle = embedStyle?.fullPageStyle;
  const popupFallback = isEmbedMode ? embedStyle : null;
  // The page background — the voice screen and the wrap-up share it. Embedded
  // with no background set anywhere, the page stays transparent over the host.
  const pageBackground = fullPageStyle?.background ?? popupBackground(popupFallback);
  const pageBackgroundStyle =
    isEmbedMode && !pageBackground ? undefined : widgetBackgroundToCss(pageBackground, "#F9FAFC");
  const chatAppearance = demoMode ? undefined : fullPageChatAppearance(embedStyle, agentLogo, popupFallback);
  const layout = fullPageStyle?.layout;
  const linksResourcesPosition = layout?.linksResourcesPosition ?? "right";
  // The Details entry is the floating tab on the voice stage. With the chat on
  // screen the tab would cover the conversation, so the entry moves to the
  // header row, or to the More menu of the full-page canvas. Mobile has no
  // floating tab, so it always uses the header row. The owner's "hidden"
  // setting removes it everywhere.
  const detailsEntryAvailable =
    linksResourcesPosition !== "hidden" && detailsProps.showDetailsAffordance && !detailsProps.showDetails;
  const chatOnScreen = !isMobile && layoutState.showTranscript;
  // Same test LiveAgentPage uses for its header "Show chat" button: the drawer
  // on mobile, the transcript column on desktop.
  const chatPanelOpen = isMobile
    ? mobileLayoutProps.mobileDrawerOpen
    : layoutState.showTranscript;
  const openDetails = () => detailsProps.setShowDetails(true);
  const avatarAlign = layout?.avatarAlign ?? "center";
  const welcomePosition = layout?.welcomePosition ?? (fullPageStyle?.hideWelcomeMessage ? "hidden" : "above-avatar");
  const transcriptPosition = layout?.transcriptPosition ?? "right";
  const avatarJustifyClass =
    avatarAlign === "left" ? "justify-start" : avatarAlign === "right" ? "justify-end" : "justify-center";

  // A plain way back to voice/chat entry from the phone/email channel stages.
  const backToGenieLink =
    channelProps.channel !== "voice" ? (
      <button
        type="button"
        onClick={() => channelProps.setChannel("voice")}
        className="mb-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#64748B] transition-colors hover:text-[#222B45]"
      >
        <ArrowLeft size={14} strokeWidth={2.25} />
        Back to the Genie
      </button>
    ) : null;

  // Each voice-stage style: the full page's own value, else (embedded only)
  // the popup's, carried in agentCoreBaseProps.
  const fullPageCoreOverrides: Partial<AgentCoreProps> = {
    statusMessageColor: fullPageStyle?.welcomeTextColor ?? undefined,
    statusMessageBelow: welcomePosition === "below-avatar",
    embedCtaStyle: fullPageStyle?.ctaButton ?? agentCoreBaseProps.embedCtaStyle,
    chatButtonStyle: fullPageStyle?.chatButton ?? popupFallback?.chatButton ?? undefined,
    embedActionButtonStyle: fullPageStyle?.actionButtons ?? agentCoreBaseProps.embedActionButtonStyle,
    embedAvatarImageUrl: fullPageStyle?.avatarImageUrl ?? agentCoreBaseProps.embedAvatarImageUrl,
    embedAvatarShowLogoOverlay: fullPageStyle?.avatarShowLogoOverlay ?? agentCoreBaseProps.embedAvatarShowLogoOverlay,
    embedAvatarSizeScale: fullPageStyle?.avatarSizeScale ?? agentCoreBaseProps.embedAvatarSizeScale,
  };

  return (
    <div
      onTouchStart={onScreenTouch}
      style={{
        position: "relative", // anchor absolute children (Back / Share / voice-trouble trigger) here
        minHeight: isEmbedMode || demoMode ? "100%" : "100dvh",
        // A definite height lets the canvas scroll its messages instead of growing the page.
        height: isEmbedMode || demoMode ? "100%" : conversationCanvasActive ? "100dvh" : "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        ...(pageBackgroundStyle ?? { background: "transparent" }),
        transition: "all 0.3s ease",
        boxSizing: "border-box",
        // Prevent "page scroll jumps" when focusing inputs on mobile, AND clip
        // edge-of-viewport hover effects (e.g. magical button sparkles) so they
        // never trigger transient scrollbars when they overflow by a few px.
        overflow: "hidden",
      }}
    >
      {helmet}
      {/* The old floating "get a summary" pill is folded into the active capture
          moments now (in-conversation card, voice-trouble sheet, wrap-up) — one
          quiet persistent entry point (the Summary action-row chip) plus active
          asks at the moments that matter, instead of a second passive nag. */}

      <StudioHeaderActions
        isInternalAdmin={isInternalAdmin}
        isEmbedMode={isEmbedMode}
        portalMode={portalMode}
        conversationCanvasActive={conversationCanvasActive}
        brand={brand}
        secondaryColor={agent?.branding?.secondaryColor}
        helpPageUrl={!demoMode ? channelProps.helpPageUrl : null}
        phoneNumber={agent?.phone?.number}
        agentName={agent?.name}
        onShowChat={
          !demoMode && agentCoreBaseProps.voiceAgent.connected && !chatPanelOpen
            ? () => layoutState.setShowTranscript(true)
            : undefined
        }
        onOpenDetails={detailsEntryAvailable && (isMobile || chatOnScreen) ? openDetails : undefined}
        onBack={headerActionsProps.onBack}
        onShare={headerActionsProps.onShare}
      />

      {/* Genie content - flex container for responsive layout */}
      {/* Keep desktop scroll inside chat/transcript panels, not on page wrapper */}
      {/* Layout follows the iframe/viewport width only — the embedded widget
          popup is sized (1240×760 via widget-v2.js) to host the desktop
          two-column layout, so embed mode and the standalone live-agent page
          render the same UI: avatar column on the left, transcript panel on
          the right, both visible at the same time. Narrow viewports (real
          phones, or a customer who manually shrinks the iframe) still fall
          back to MobileLiveAgentLayout. */}
      {/* The top padding keeps the mobile stage clear of the header buttons
          (back, chat, links, support, share), as on LiveAgentPage. */}
      <div className={cn("min-h-0 flex-1 flex w-full box-border overflow-hidden", !isEmbedMode && !demoMode && "pt-20 md:pt-0")}>
        {isMobile ? (
          <MobileLiveAgentLayout
            chatAppearance={chatAppearance}
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            messages={layoutState.chatMessages}
            forceTranscriptOpen={mobileLayoutProps.forceTranscriptOpen}
            suppressTranscript={mobileLayoutProps.suppressTranscript}
            resourcesRail={
              // Before a conversation the support page holds the guides, so
              // the rail only shows once one is live (as on LiveAgentPage and
              // in the desktop gutter).
              agentCoreBaseProps.voiceAgent.connected && resourcesRail.railItems.length > 0 && !demoMode ? (
                <ResourcesRail
                  resources={resourcesRail.resourcesConfig}
                  brandColor={brand}
                  variant="horizontal"
                  compact={agentCoreBaseProps.voiceAgent.connected}
                  surfacedId={resourcesRail.surfacedResourceId}
                  onOpenAll={resourcesRail.onOpenAll}
                  onSelect={resourcesRail.onSelect}
                  onImpression={resourcesRail.onImpression}
                />
              ) : null
            }
            rightInlineIcons={
              <div className="flex flex-col items-center gap-4">
                <StudioActionCluster
                  brandColor={brand}
                  secondaryColor={agent?.branding?.secondaryColor}
                  isMobile={true}
                  voiceConnected={agentCoreBaseProps.voiceAgent.connected}
                  isConnecting={agentCoreBaseProps.voiceAgent.isConnecting}
                  onStartVoice={agentCoreBaseProps.onAvatarClick}
                  onStartText={agentCoreBaseProps.onStartText ?? (() => layoutState.setShowTranscript(true))}
                  onEndConversation={agentCoreBaseProps.onAvatarClick}
                  onToggleChat={() => layoutState.setShowTranscript(!layoutState.showTranscript)}
                  chatOpen={layoutState.showTranscript}
                  isMicMuted={layoutState.isMicMuted}
                  onToggleMicMute={layoutState.onToggleMicMute}
                  isAgentVoiceMuted={layoutState.isAgentVoiceMuted}
                  onToggleAgentVoice={layoutState.onToggleAgentVoice}
                  isPaused={layoutState.isPaused}
                  onTogglePause={layoutState.onTogglePause}
                  onAddImage={agentCoreBaseProps.onAddImage}
                  isTextOnlyMode={layoutState.isTextOnlyMode}
                  onTalkToHuman={
                    agentCoreBaseProps.showHumanHandoffButton &&
                    agentCoreBaseProps.voiceBridge.bridgeStatus === "idle"
                      ? agentCoreBaseProps.onOpenHandoffPrompt
                      : undefined
                  }
                  phoneNumber={channelProps.phoneNumber}
                  onCallGenie={channelProps.onCallGenie}
                  mailboxAddress={channelProps.mailboxAddress}
                  onEmailGenie={channelProps.onEmailGenie}
                  actionButtonStyle={fullPageCoreOverrides.embedActionButtonStyle}
                  ctaButtonStyle={fullPageCoreOverrides.embedCtaStyle}
                  ctaShape={agentCoreBaseProps.embedCtaShape}
                  chatButtonStyle={fullPageCoreOverrides.chatButtonStyle}
                />
              </div>
            }
            drawerHeaderVoiceControls={
              /* Compact mode for the slim drawer header:
                 - neutralise VoiceSessionActionRow's mt-4 lg:mt-8 + px-3
                 - hide the per-button text labels (hover tooltips still work)
                   so the row matches the icon-only cog + close on the right
                 - keep tight horizontal spacing
                 - align bottom so the 36px buttons sit on the same baseline as
                   the 32px cog/close on the right side of the header */
              <div className="-my-1 [&>div]:!mt-0 [&>div]:!px-0 [&>div>div]:!gap-2 [&_span[class*='max-w-\\[5\\.5rem\\]']]:!hidden">
                <VoiceSessionActionRow
                  brandColor={brand}
                  isMobile={true}
                  isTranscriptFeatureEnabled
                  voiceConnected={agentCoreBaseProps.voiceAgent.connected}
                  onEndConversation={agentCoreBaseProps.onAvatarClick}
                  isTextOnlyMode={layoutState.isTextOnlyMode}
                  isMicMuted={layoutState.isMicMuted}
                  onToggleMicMute={layoutState.onToggleMicMute}
                  isAgentVoiceMuted={layoutState.isAgentVoiceMuted}
                  onToggleAgentVoice={layoutState.onToggleAgentVoice}
                  isPaused={layoutState.isPaused}
                  onTogglePause={layoutState.onTogglePause}
                  onOpenChat={() => layoutState.setShowTranscript(!layoutState.showTranscript)}
                  chatOpen={layoutState.showTranscript}
                  infoTitle={`About ${agent?.name || "this Genie"}`}
                  infoDescription={agent?.description || null}
                  infoLinks={agent?.page?.info_links as InfoLinksConfig | null}
                  qrLogoUrl={agent?.branding?.logoUrl || null}
                  showSummaryCapture={false}
                  actionButtonStyle={fullPageCoreOverrides.embedActionButtonStyle}
                />
              </div>
            }
            isConnected={agentCoreBaseProps.voiceAgent.connected}
            isListening={agentCoreBaseProps.voiceAgent.listening}
            isConnecting={agentCoreBaseProps.voiceAgent.isConnecting}
            isSpeaking={agentCoreBaseProps.voiceAgent.speaking}
            isAllAudioMuted={layoutState.isAllAudioMuted}
            onToggleAllAudioMute={layoutState.onToggleAllAudioMute}
            isTextOnlyMode={layoutState.isTextOnlyMode}
            inlineMessage={layoutState.inlineMessage}
            onChangeInlineMessage={layoutState.setInlineMessage}
            onSendChat={layoutState.onSendChat}
            startedFromText={layoutState.startedFromText}
            onEndChat={layoutState.onEndChat}
            attachments={mappedAttachments}
            onRemoveAttachment={attachmentsState.onRemoveAttachment}
            onOpenFilePicker={openFilePicker}
            onUpdateAttachmentAnalysis={attachmentsState.onUpdateAttachmentAnalysis}
            imageUploadPendingCount={attachmentsState.pendingImageUploads}
            imageUploadFailedCount={attachmentsState.failedImageUploads}
            imageUploadSaving={attachmentsState.isSavingImages}
            onRetryImageUploads={attachmentsState.onRetryImageUploads}
            onDrawerOpenChange={mobileLayoutProps.onDrawerOpenChange}
            belowActionRow={
              mobileLayoutProps.sharedMedia ? (
                <div className="flex w-full justify-center">{mobileLayoutProps.sharedMedia}</div>
              ) : undefined
            }
            inlineCaptureSlot={layoutState.inlineCaptureNode}
          >
            {/* Status message + avatar + CTA + handoff button/bar. The mic/chat/
                voice action row is rendered separately above, via rightInlineIcons
                and drawerHeaderVoiceControls, so it's turned off here. Swapped
                out for the Call/Email stages, which have no voice status/avatar
                of their own. */}
            {channelProps.channel === "phone" ? (
              <>
                {backToGenieLink}
                <CallStage
                  brandColor={brand}
                  secondaryColor={agent?.branding?.secondaryColor}
                  agentName={agent?.name}
                  phoneNumber={channelProps.phoneNumber}
                  callbackAvailable={channelProps.callbackAvailable}
                  onRequestCallback={channelProps.onRequestCallback}
                  pageUrl={channelProps.channelPageUrl}
                />
              </>
            ) : channelProps.channel === "email" ? (
              <>
                {backToGenieLink}
                <EmailStage
                  brandColor={brand}
                  secondaryColor={agent?.branding?.secondaryColor}
                  agentName={agent?.name}
                  onSubmit={channelProps.onAskQuestion}
                  pageUrl={channelProps.channelPageUrl}
                />
              </>
            ) : (
              <AgentCore
                {...agentCoreBaseProps}
                {...fullPageCoreOverrides}
                statusMessage={getStatusMessage()}
                hideStatusMessage={welcomePosition === "hidden"}
                avatarSize={isEmbedMode ? 200 : undefined}
                showActionRow={false}
                useAlternateCluster
                onOpenChat={() => layoutState.setShowTranscript(true)}
              />
            )}
          </MobileLiveAgentLayout>
        ) : null}

        {!isMobile && (conversationCanvasActive ? (
          <GenieConversationCanvas
            {...chatAppearance}
            onRestoreSplitView={followUpProps.fullTextView ? () => followUpProps.setFullTextView(false) : undefined}
            audioControls={
              followUpProps.fullTextView && agentCoreBaseProps.voiceAgent.connected && !layoutState.startedFromText ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-11 w-11 rounded-full p-0"
                    onClick={layoutState.onToggleMicMute}
                    aria-label={layoutState.isMicMuted ? "Unmute microphone" : "Mute microphone"}
                    title={layoutState.isMicMuted ? "Unmute microphone" : "Mute microphone"}
                    aria-pressed={layoutState.isMicMuted}
                  >
                    {layoutState.isMicMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-11 w-11 rounded-full p-0"
                    onClick={layoutState.onToggleAgentVoice}
                    aria-label={layoutState.isAgentVoiceMuted ? "Unmute Genie voice" : "Mute Genie voice"}
                    title={layoutState.isAgentVoiceMuted ? "Unmute Genie voice" : "Mute Genie voice"}
                    aria-pressed={layoutState.isAgentVoiceMuted}
                  >
                    {layoutState.isAgentVoiceMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </Button>
                </>
              ) : undefined
            }
            helpPageUrl={channelProps.helpPageUrl}
            onOpenDetails={detailsEntryAvailable ? openDetails : undefined}
            onEmailSummary={followUpProps.onEmailSummary}
            emailSummaryPending={followUpProps.emailSummaryPending}
            emailSummaryRequested={followUpProps.emailSummaryRequested}
            onEmailFollowUp={followUpProps.onEmailFollowUp}
            onCallGenie={channelProps.phoneNumber ? channelProps.onCallGenie : undefined}
            onTalkToHuman={
              agentCoreBaseProps.showHumanHandoffButton && agentCoreBaseProps.voiceBridge.bridgeStatus === "idle"
                ? agentCoreBaseProps.onOpenHandoffPrompt
                : undefined
            }
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            logoUrl={agentLogo}
            messages={layoutState.chatMessages}
            inlineMessage={layoutState.inlineMessage}
            onChangeInlineMessage={layoutState.setInlineMessage}
            onSend={layoutState.onSendChat}
            isConnecting={agentCoreBaseProps.voiceAgent.isConnecting}
            isSpeaking={agentCoreBaseProps.voiceAgent.speaking}
            startedFromText={layoutState.startedFromText}
            voiceConnected={agentCoreBaseProps.voiceAgent.connected}
            onEndConversation={layoutState.onEndConversation}
            onEndChat={layoutState.onEndChat}
            onCloseOverlay={() => {
              layoutState.setShowTranscript(false);
              layoutState.setChatExpanded(false);
            }}
            attachments={mappedAttachments}
            onRemoveAttachment={attachmentsState.onRemoveAttachment}
            onOpenFilePicker={openFilePicker}
            onUpdateAttachmentAnalysis={attachmentsState.onUpdateAttachmentAnalysis}
            imageUploadPendingCount={attachmentsState.pendingImageUploads}
            imageUploadFailedCount={attachmentsState.failedImageUploads}
            imageUploadSaving={attachmentsState.isSavingImages}
            onRetryImageUploads={attachmentsState.onRetryImageUploads}
            inlineCaptureSlot={layoutState.inlineCaptureNode}
          />
        ) : (
          <motion.div
            layout={!layoutState.startedFromText}
            className={cn(
              "relative flex min-h-0 w-full mx-auto items-center gap-6 px-6",
              // Framed, the viewport units can exceed the frame, so the stage
              // takes its height from the frame itself.
              isEmbedMode ? "h-full" : "h-[calc(100dvh-60px)]",
              // The rail adds a third column, so the frame needs room for it
              // on top of the genie + transcript columns.
              resourcesRail.railInGutter
                ? layoutState.chatExpanded
                  ? "max-w-[1820px]"
                  : "max-w-[1600px]"
                : layoutState.chatExpanded
                  ? "max-w-[1500px]"
                  : "max-w-[1280px]",
              transcriptPosition === "bottom" ? "flex-col" : "flex-row",
              transcriptPosition === "left" && "flex-row-reverse",
              avatarJustifyClass,
            )}
          >
            {/* Full-screen transcript layout is used for conversations started
                from text, OR whenever fullPageStyle.layout.transcriptPosition
                is 'overlay' (Studio-only) — both take over the screen instead
                of sitting beside the avatar column. */}
            {(() => {
              const useTextOnlyDesktopLayout = layoutState.startedFromText || transcriptPosition === "overlay";
              // Chat-first focus mode: shrink the genie column, grow the chat column.
              // Genie + branding stay visible — just compact. Column needs to fit the
              // End button (minWidth 160) + breathing room around the 160px avatar.
              const chatVisible =
                layoutState.showTranscript &&
                (layoutState.isTranscriptEnabled() || layoutState.isTextOnlyMode || isEmbedMode);
              const rightColumnVisible = chatVisible;
              const sharedMediaColumnVisible = !rightColumnVisible && !!sharedMediaColumn;
              const chatBeside =
                rightColumnVisible && !useTextOnlyDesktopLayout && transcriptPosition !== "bottom";
              // The rail keeps its gutter only while every column fits the
              // viewport; otherwise it lays down as a band under the action
              // row, or its resources show as cards in the chat once that is open.
              const railGutterVisible =
                resourcesRail.railInGutter &&
                !(rightColumnVisible && useTextOnlyDesktopLayout) &&
                (chatBeside || sharedMediaColumnVisible ? stageFit.railBesideSideColumn : stageFit.railGutters);
              // Too narrow for the full genie column beside the transcript:
              // fall back to the chat-focus sizes so nothing leaves the viewport.
              const compactGenie =
                layoutState.chatExpanded || (chatBeside && !stageFit.fullGenieBesideSideColumn);
              const genieColumnWidth = compactGenie ? 240 : 620;
              const chatColumnClass = layoutState.chatExpanded
                ? "flex-[0_1_980px] min-w-[360px]"
                : "flex-[0_1_560px] min-w-[360px]";
              const avatarSize = compactGenie ? 160 : undefined;
              return (
                <AnimatePresence initial={false}>
                  {railGutterVisible && (
                    <div key="desktop-resources-rail" className={RAIL_GUTTER_CLASS}>
                      {/* Pre-conversation, the support-page link covers guides
                          and resources — the gutter itself stays reserved (so the
                          layout never jumps) but the rail only mounts once a
                          conversation is actually live. */}
                      {agentCoreBaseProps.voiceAgent.connected && (
                        <ResourcesRail
                          resources={resourcesRail.resourcesConfig}
                          brandColor={brand}
                          variant="vertical"
                          compact={agentCoreBaseProps.voiceAgent.connected}
                          surfacedId={resourcesRail.surfacedResourceId}
                          onOpenAll={resourcesRail.onOpenAll}
                          onSelect={resourcesRail.onSelect}
                          onImpression={resourcesRail.onImpression}
                        />
                      )}
                    </div>
                  )}

                  {!(layoutState.showTranscript && useTextOnlyDesktopLayout) && (
                    <motion.div
                      key="desktop-voice-core"
                      layout
                      initial={{ opacity: 0, scaleX: 0.9 }}
                      animate={{ opacity: 1, scaleX: 1 }}
                      exit={{ opacity: 0, scaleX: 0.9 }}
                      transition={{
                        duration: 0.22,
                        ease: "easeOut",
                        layout: { duration: 0.24, ease: "easeInOut" },
                      }}
                    >
                      <LiveAgentContainer
                        showTranscript={false}
                        isMobile={false}
                        brandColor={brand}
                        agentName={agent?.name}
                        messages={layoutState.messages}
                        onCloseTranscript={() => layoutState.setShowTranscript(false)}
                        leftWidthPx={genieColumnWidth}
                        transcriptWidthPx={460}
                        gapPx={16}
                      >
                        {/* Status message hidden, avatar shrunk, and CTA compacted
                            in chat-focus mode to keep the genie column tight. The
                            mic/chat/voice row stays visible throughout — those
                            controls matter most mid-conversation. Swapped out for
                            the Call/Email stages, which have no voice status/
                            avatar of their own. */}
                        {channelProps.channel === "phone" ? (
                          <>
                            {backToGenieLink}
                            <CallStage
                              brandColor={brand}
                              secondaryColor={agent?.branding?.secondaryColor}
                              agentName={agent?.name}
                              phoneNumber={channelProps.phoneNumber}
                              callbackAvailable={channelProps.callbackAvailable}
                              onRequestCallback={channelProps.onRequestCallback}
                              pageUrl={channelProps.channelPageUrl}
                            />
                          </>
                        ) : channelProps.channel === "email" ? (
                          <>
                            {backToGenieLink}
                            <EmailStage
                              brandColor={brand}
                              secondaryColor={agent?.branding?.secondaryColor}
                              agentName={agent?.name}
                              onSubmit={channelProps.onAskQuestion}
                              pageUrl={channelProps.channelPageUrl}
                            />
                          </>
                        ) : (
                          <AgentCore
                            {...agentCoreBaseProps}
                            {...fullPageCoreOverrides}
                            statusMessage={getStatusMessage()}
                            hideStatusMessage={compactGenie || welcomePosition === "hidden"}
                            avatarSize={avatarSize}
                            ctaCompact={compactGenie}
                            ctaClassName={compactGenie ? "mt-3" : "mt-6 md:mt-8"}
                            isMobile={false}
                            showInfoActions={false}
                            onOpenChat={() => layoutState.setShowTranscript(!layoutState.showTranscript)}
                            useAlternateCluster={!demoMode}
                            chatOpen={layoutState.showTranscript}
                          />
                        )}

                        {/* Without room for a third column (the embed popup,
                            narrower screens) the rail lays down as a band under
                            the action row rather than taking a gutter. Same
                            component, same items. */}
                        {resourcesRail.railInGutter &&
                          !railGutterVisible &&
                          !rightColumnVisible &&
                          (isEmbedMode || agentCoreBaseProps.voiceAgent.connected) &&
                          resourcesRail.railItems.length > 0 &&
                          !compactGenie && (
                          <div className="mt-5 w-full text-left">
                            <ResourcesRail
                              resources={resourcesRail.resourcesConfig}
                              brandColor={brand}
                              variant="horizontal"
                              compact={agentCoreBaseProps.voiceAgent.connected}
                              surfacedId={resourcesRail.surfacedResourceId}
                              onOpenAll={resourcesRail.onOpenAll}
                              onSelect={resourcesRail.onSelect}
                              onImpression={resourcesRail.onImpression}
                            />
                          </div>
                        )}
                      </LiveAgentContainer>
                    </motion.div>
                  )}

                  {rightColumnVisible && (
                    <motion.div
                      key="desktop-chat-overlay"
                      layout={!useTextOnlyDesktopLayout}
                      initial={useTextOnlyDesktopLayout ? { opacity: 0, y: 10 } : { opacity: 0, scaleX: 0.9 }}
                      animate={useTextOnlyDesktopLayout ? { opacity: 1, y: 0 } : { opacity: 1, scaleX: 1 }}
                      exit={useTextOnlyDesktopLayout ? { opacity: 0, y: 10 } : { opacity: 0, scaleX: 0.9 }}
                      transition={{
                        duration: useTextOnlyDesktopLayout ? 0.3 : 0.22,
                        ease: "easeOut",
                        layout: { duration: 0.24, ease: "easeInOut" },
                      }}
                      style={useTextOnlyDesktopLayout ? undefined : { originX: 0 }}
                      className={cn(
                        "relative",
                        useTextOnlyDesktopLayout
                          ? cn(
                              "flex min-h-0 h-full w-full max-w-[860px] items-center justify-center",
                              isEmbedMode ? "max-h-[calc(100%-30px)]" : "max-h-[calc(100dvh-90px)]",
                            )
                          : transcriptPosition === "bottom"
                            ? "w-full flex-[1_1_auto]"
                            : chatColumnClass,
                      )}
                    >
                      {chatVisible && (
                        <GenieTextChatOverlayWeb
                          {...chatAppearance}
                          brandColor={brand}
                          secondaryColor={agent?.branding?.secondaryColor}
                          agentName={agent?.name}
                          // The gutter rail already shows the resources.
                          messages={railGutterVisible ? layoutState.messages : layoutState.chatMessages}
                          inlineMessage={layoutState.inlineMessage}
                          onChangeInlineMessage={layoutState.setInlineMessage}
                          onSend={layoutState.onSendChat}
                          isConnecting={agentCoreBaseProps.voiceAgent.isConnecting}
                          isSpeaking={agentCoreBaseProps.voiceAgent.speaking}
                          isTextOnlyMode={layoutState.isTextOnlyMode}
                          isAllAudioMuted={layoutState.isAllAudioMuted}
                          onToggleAllAudioMute={layoutState.onToggleAllAudioMute}
                          startedFromText={layoutState.startedFromText}
                          onCloseOverlay={() => {
                            layoutState.setShowTranscript(false);
                            layoutState.setChatExpanded(false);
                          }}
                          onEndChat={layoutState.onEndChat}
                          chatExpanded={!useTextOnlyDesktopLayout && layoutState.chatExpanded}
                          onToggleExpand={
                            useTextOnlyDesktopLayout ? undefined : () => layoutState.setChatExpanded((v) => !v)
                          }
                          onOpenFullTextView={
                            !isEmbedMode && !demoMode && !useTextOnlyDesktopLayout
                              ? () => followUpProps.setFullTextView(true)
                              : undefined
                          }
                          attachments={mappedAttachments}
                          onRemoveAttachment={attachmentsState.onRemoveAttachment}
                          onOpenFilePicker={openFilePicker}
                          onUpdateAttachmentAnalysis={attachmentsState.onUpdateAttachmentAnalysis}
                          voiceConnected={agentCoreBaseProps.voiceAgent.connected}
                          onEndConversation={
                            // During a live conversation the header shows the close (X)
                            // button, as on LiveAgentPage: ending is the action row's
                            // job, and a chat started from text gets "End chat".
                            !demoMode && agentCoreBaseProps.voiceAgent.connected
                              ? undefined
                              : layoutState.onEndConversation
                          }
                          imageUploadPendingCount={attachmentsState.pendingImageUploads}
                          imageUploadFailedCount={attachmentsState.failedImageUploads}
                          imageUploadSaving={attachmentsState.isSavingImages}
                          onRetryImageUploads={attachmentsState.onRetryImageUploads}
                          surface={useTextOnlyDesktopLayout ? "card" : "bare"}
                          logoUrl={agentLogo}
                          inlineCaptureSlot={layoutState.inlineCaptureNode}
                        />
                      )}
                    </motion.div>
                  )}

                  {/* With the transcript closed, what the Genie shares takes its
                      place beside the avatar instead of stacking under it. */}
                  {sharedMediaColumnVisible && (
                    <motion.div
                      key="desktop-shared-media"
                      layout
                      initial={{ opacity: 0, scaleX: 0.9 }}
                      animate={{ opacity: 1, scaleX: 1 }}
                      exit={{ opacity: 0, scaleX: 0.9 }}
                      transition={{
                        duration: 0.22,
                        ease: "easeOut",
                        layout: { duration: 0.24, ease: "easeInOut" },
                      }}
                      style={{ originX: 0 }}
                      className={SHARED_MEDIA_COLUMN_CLASS}
                    >
                      {sharedMediaColumn}
                    </motion.div>
                  )}

                  {/* Keeps the genie column optically centred when the rail is
                      in the left gutter but the transcript column is closed. */}
                  {railGutterVisible &&
                    avatarAlign === "center" &&
                    !rightColumnVisible &&
                    !sharedMediaColumnVisible && (
                      <div key="desktop-rail-spacer" aria-hidden className={RAIL_GUTTER_CLASS} />
                    )}
                </AnimatePresence>
              );
            })()}
          </motion.div>
        ))}
      </div>

      {/* Bottom text chat input — only when voice session active and mic muted */}
      <AnimatePresence>
        {/* An attachment on its own is enough to bring the composer up. Without
            that, "Add photo" from the action row would analyse the image and
            then show the visitor nothing to send it with. */}
        {((agentCoreBaseProps.voiceAgent.connected && layoutState.isMicMuted) ||
          attachmentsState.attachments.length > 0) &&
          !layoutState.isPaused &&
          !layoutState.showTranscript &&
          !(isMobile && mobileLayoutProps.mobileDrawerOpen) && (
          <MobileAwareBottomInput
            isMobile={isMobile}
            inlineMessage={layoutState.inlineMessage}
            setInlineMessage={layoutState.setInlineMessage}
            onSend={layoutState.onSendChat}
            attachments={attachmentsState.attachments}
            removeAttachment={attachmentsState.onRemoveAttachment}
            updateAttachmentAnalysis={attachmentsState.onUpdateAttachmentAnalysis}
          />
        )}
      </AnimatePresence>

      <EmbedAttributionFooter
        isEmbedMode={isEmbedMode}
        hideAttribution={agent?.branding?.hideAttribution}
        brand={brand}
        studioUrl={studioUrl}
      />

      {/* iPhone tap reminder */}
      <IPhoneTapReminder
        show={modalsProps.tapReminder.showReminder}
        onDismiss={modalsProps.tapReminder.dismissReminder}
      />

      <NoiseEnvironmentSheet
        open={modalsProps.showNoiseWarning}
        brandColor={brand}
        onDismiss={modalsProps.dismissNoiseWarning}
        onSwitchToText={modalsProps.onNoiseSwitchToText}
      />

      <ResourcesSheet
        open={modalsProps.showResources}
        onClose={modalsProps.onCloseResources}
        brandColor={brand}
        resources={resourcesRail.resourcesConfig}
      />

      <ConversationFollowUpSheet
        open={followUpProps.open}
        onClose={followUpProps.onClose}
        form={followUpProps.form}
        purpose={followUpProps.purpose}
        contextTruncated={followUpProps.contextTruncated}
        onSubmit={followUpProps.onSubmit}
        isPending={followUpProps.isPending}
        isSuccess={followUpProps.isSuccess}
        error={followUpProps.error}
      />

      {/* Voice-trouble fallback — subtle manual trigger while live, plus an
          automatic offer when cheap frontend signals suggest voice is
          struggling (see useVoiceTroubleSignals). A rescued wobble is a lead. */}
      {modalsProps.showVoiceTroubleTrigger && (
        <div
          className="absolute inset-x-0 z-[850] flex justify-center"
          style={{ bottom: "max(14px, env(safe-area-inset-bottom))" }}
        >
          <VoiceTroubleTrigger onClick={() => modalsProps.setShowVoiceTrouble(true)} />
        </div>
      )}
      <VoiceTroubleSheet
        open={modalsProps.showVoiceTrouble}
        onDismiss={() => modalsProps.setShowVoiceTrouble(false)}
        brandColor={brand}
        secondaryColor={agent?.branding?.secondaryColor}
        agentId={agent?.id ?? undefined}
        agentUserId={agent?.user_id ?? undefined}
        phoneNumber={modalsProps.phoneNumber}
        onSwitchToText={modalsProps.onNoiseSwitchToText}
        onSummaryCaptured={modalsProps.onContactCaptured}
      />

      {/* Frosted-glass veil — the start screen softens into haze as the Details
          view is summoned, so the two states blur into each other rather than
          hard-cutting. */}
      <AnimatePresence>
        {detailsProps.showDetails && (
          <motion.div
            key="details-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="absolute inset-0 z-[1400]"
            style={{
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              background: "rgba(249,250,252,0.35)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Right-edge Details control → summons the Details view. Styled to match
          the bottom action row (circular button + label). It dissolves away as
          the panel forms and reforms as the panel clears, so the loop is seamless.
          Desktop only — on mobile this floats over the avatar, so the Details
          entry is folded into the bottom action row instead. */}
      <AnimatePresence>
        {detailsEntryAvailable &&
          !isMobile &&
          !chatOnScreen &&
          !conversationCanvasActive && (
            <motion.div
              key="details-tab"
              initial={{ opacity: 0, x: linksResourcesPosition === "left" ? -16 : 16, y: "-50%" }}
              animate={{ opacity: 1, x: 0, y: "-50%" }}
              exit={{ opacity: 0, x: linksResourcesPosition === "left" ? -16 : 16, y: "-50%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={cn(
                "absolute top-1/2 z-[1000]",
                linksResourcesPosition === "left" ? "left-5 sm:left-7" : "right-5 sm:right-7",
              )}
            >
              <GenieDetailsTab
                onClick={() => detailsProps.setShowDetails(true)}
                brandColor={brand}
                secondaryColor={agent?.branding?.secondaryColor}
              />
            </motion.div>
          )}
      </AnimatePresence>

      <GenieDetailsPanel
        open={detailsProps.showDetails}
        onClose={() => detailsProps.setShowDetails(false)}
        brandColor={brand}
        secondaryColor={agent?.branding?.secondaryColor}
        agentName={agent?.name}
        agentDescription={agent?.description || null}
        logoUrl={agentLogo || null}
        resources={resourcesRail.resourcesConfig}
        qrLogoUrl={agent?.branding?.logoUrl || null}
        agentId={agent?.id ?? undefined}
        agentUserId={agent?.user_id ?? undefined}
        helpPageUrl={detailsProps.helpPageUrl}
        helpHub={detailsProps.helpHub}
        onSummaryCaptured={modalsProps.onContactCaptured}
        memoryConsent={detailsProps.canManageCustomerMemory ? agent?.memoryConsent || "unset" : undefined}
        memoryUpdating={detailsProps.memoryUpdating}
        onMemoryConsentChange={
          detailsProps.canManageCustomerMemory ? (enabled) => detailsProps.onMemoryConsentChange(enabled) : undefined
        }
      />

      {/* End-of-conversation wrap-up — poofs in when a call ends (standalone page
          only; redirect/embed keep their own exit rules). Configured
          redirects retain priority; a published help page takes over once
          active saves and any visitor-opened email follow-up finish. */}
      {shouldRedirectToHelpPage({
        showWrapUp: wrapUpProps.showWrapUp,
        helpPageUrl: detailsProps.helpPageUrl,
        agentRedirectUrl: agent?.redirect_url,
        voiceConnected: agentCoreBaseProps.voiceAgent.connected,
        voiceIsConnecting: agentCoreBaseProps.voiceAgent.isConnecting,
        isSavingImages: attachmentsState.isSavingImages,
        pendingImageUploads: attachmentsState.pendingImageUploads,
        failedImageUploads: attachmentsState.failedImageUploads,
        followUpOpen: followUpProps.open,
        followUpIsPending: followUpProps.isPending,
      }) ? (
        <Navigate to={detailsProps.helpPageUrl as string} replace />
      ) : (
        <ConversationWrapUp
          open={wrapUpProps.showWrapUp && !agentCoreBaseProps.voiceAgent.connected && !agentCoreBaseProps.voiceAgent.isConnecting}
          backgroundStyle={pageBackgroundStyle}
          textColor={fullPageStyle?.welcomeTextColor}
          darkSurface={isDarkBackground(pageBackground)}
          onClose={() => {
            wrapUpProps.setShowWrapUp(false);
            wrapUpProps.onStartOver();
          }}
          brandColor={brand}
          secondaryColor={agent?.branding?.secondaryColor}
          agentName={agent?.name}
          logoUrl={agentLogo || null}
          resources={resourcesRail.resourcesConfig}
          qrLogoUrl={agent?.branding?.logoUrl || null}
          agentId={agent?.id ?? undefined}
          agentUserId={agent?.user_id ?? undefined}
          conversationId={modalsProps.lastConversationId}
          transcript={layoutState.messages}
          onSummaryCaptured={modalsProps.onContactCaptured}
          phoneNumber={modalsProps.phoneNumber}
          helpPageUrl={detailsProps.helpPageUrl}
          onSwitchToText={wrapUpProps.onSwitchToText}
        />
      )}

      {handoffProps.isVoiceBridgeEnabled && (
        <HumanHandoffPrompt
          open={handoffProps.showHandoffPrompt}
          onOpenChange={(open) => {
            handoffProps.setShowHandoffPrompt(open);
            if (!open) handoffProps.setHandoffError(null);
          }}
          brandColor={brand}
          agentName={agent?.name}
          isConnecting={
            handoffProps.bridgeStatus === "requesting-token" || handoffProps.bridgeStatus === "connecting"
          }
          error={handoffProps.handoffError}
          onConfirm={handoffProps.onConfirmHandoff}
          agentId={agent?.id ?? undefined}
          agentUserId={agent?.user_id ?? undefined}
          onSummaryCaptured={modalsProps.onContactCaptured}
        />
      )}

      {/* "What to expect" welcome gate — sits over the calm start screen the first
          time a visitor opens this Genie so the voice never ambushes them. */}
      <GenieWelcomeScreen
        open={
          welcomeProps.showWelcome &&
          channelProps.channel === "voice" &&
          (!welcomeProps.welcomeSeenForGenie ||
            (welcomeProps.canManageCustomerMemory && agent?.memoryConsent === "unset")) &&
          !isEmbedMode &&
          !demoMode &&
          !welcomeProps.showInviteGate &&
          !agentCoreBaseProps.voiceAgent.connected &&
          !agentCoreBaseProps.voiceAgent.isConnecting
        }
        brandColor={brand}
        secondaryColor={agent?.branding?.secondaryColor}
        agentName={agent?.name}
        agentDescription={agent?.description || null}
        logoUrl={agentLogo || null}
        hideAttribution={agent?.branding?.hideAttribution}
        hasPhone={!!channelProps.phoneNumber}
        hasEmail={!!channelProps.mailboxAddress}
        phoneNumber={channelProps.phoneNumber}
        emailAddress={channelProps.mailboxAddress}
        pageUrl={studioUrl}
        urlName={agent?.url_name}
        agentId={agent?.id ?? undefined}
        agentUserId={agent?.user_id ?? undefined}
        onSummaryCaptured={modalsProps.onContactCaptured}
        memoryConsent={welcomeProps.canManageCustomerMemory ? agent?.memoryConsent || "unset" : undefined}
        memoryUpdating={detailsProps.memoryUpdating}
        onMemoryConsentChange={
          welcomeProps.canManageCustomerMemory ? (enabled) => detailsProps.onMemoryConsentChange(enabled) : undefined
        }
        onStartVoice={welcomeProps.onStartVoice}
        onStartText={welcomeProps.onStartText}
      />

      <PauseOverlay {...pauseOverlayProps} />

      {/* Hidden file input for image attachments */}
      <input
        id="genie-chat-attachments"
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => {
          attachmentsState.onAddFiles(event.target.files);
          // Reset input value so selecting the same file again still triggers onChange.
          event.target.value = "";
        }}
      />

      {/* Second input, camera-first. A separate element rather than setting
          `capture` on the one above and clicking in the same tick: that races
          React's reconciliation, and Safari caches the picker configuration on
          first open. `capture` is ignored on desktop, and `multiple` is
          meaningless alongside it, so it is left off. */}
      <input
        id="genie-chat-camera"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          attachmentsState.onAddFiles(event.target.files);
          event.target.value = "";
        }}
      />
    </div>
  );
}

export default StudioDefaultBranch;
